import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import SimplePriceComparison from './SimplePriceComparison';

// Simple product page with minimal content, just product name and price comparison
const SimpleProductPage = ({ specificProductId, productName, productImage, fallbackImage, csvLookupId, csvExactMatchOnly = false }) => {
  const { productId: urlProductId } = useParams();
  const productId = specificProductId || urlProductId;
  const { theme } = useTheme();
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  const [imageSrc, setImageSrc] = useState(productImage);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  
  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Update image source when productImage prop changes
  useEffect(() => {
    setImageSrc(productImage);
    setImageLoaded(false);
    setImageError(false);
  }, [productImage]);
  
  // Log image URL for debugging
  useEffect(() => {
    console.log('SimpleProductPage - Product ID:', productId);
    console.log('SimpleProductPage - Product Name:', productName);
    console.log('SimpleProductPage - Product Image URL:', productImage);
    console.log('SimpleProductPage - CSV Lookup ID:', csvLookupId);
    console.log('SimpleProductPage - Exact Match Only:', csvExactMatchOnly);
    console.log('SimpleProductPage - Current Image State:', { 
      imageSrc, 
      imageLoaded, 
      imageError,
      publicUrl: `${process.env.PUBLIC_URL}${productImage?.startsWith('/') ? productImage : `/${productImage}`}`
    });
    
    // Try to preload the image to test if it exists
    if (productImage) {
      const img = new Image();
      img.onload = () => {
        console.log('Image preload successful:', productImage);
        setImageLoaded(true);
        setImageError(false);
      };
      img.onerror = () => {
        console.error('Image preload failed:', productImage);
        setImageError(true);
        // Try with PUBLIC_URL prefix
        const withPublicUrl = `${process.env.PUBLIC_URL}${productImage?.startsWith('/') ? productImage : `/${productImage}`}`;
        setImageSrc(withPublicUrl);
      };
      img.src = productImage;
    }
  }, [productId, productName, productImage, imageSrc, imageLoaded, imageError, csvLookupId, csvExactMatchOnly]);
  
  // Get product name from props or format from productId
  const getDisplayName = () => {
    if (productName) return productName;
    if (!productId) return 'Product Details';
    
    // Format product ID for display with proper capitalization for ECHOMAP and UHD/UHD2
    const formattedName = productId
      .split('-')
      .map(word => {
        // Special case for ECHOMAP - always all caps
        if (word.toLowerCase() === 'echomap') return 'ECHOMAP';
        
        // Special case for UHD/UHD2 - always all caps
        if (word.toLowerCase() === 'uhd') return 'UHD';
        if (word.toLowerCase() === 'uhd2') return 'UHD2';
        
        // Default capitalization for other words
        return word.charAt(0).toUpperCase() + word.slice(1);
      })
      .join(' ');
    
    return formattedName;
  };
  
  // Handle image loading error
  const handleImageError = (e) => {
    console.error('Image failed to load:', imageSrc);
    setImageError(true);
    e.target.onerror = null;
    
    // Try different image paths as fallbacks
    if (imageSrc === productImage) {
      // Try with PUBLIC_URL prefix
      const withPublicUrl = `${process.env.PUBLIC_URL}${productImage?.startsWith('/') ? productImage : `/${productImage}`}`;
      console.log('Trying fallback image path:', withPublicUrl);
      setImageSrc(withPublicUrl);
    } else if (fallbackImage) {
      // Use the provided fallback image (e.g. base64 encoded)
      console.log('Using provided fallback image');
      setImageSrc(fallbackImage);
    } else {
      // Last resort - use a dummy image
      e.target.src = `https://dummyimage.com/400x200/cccccc/000000&text=${getDisplayName()}`;
    }
  };
  
  // Handle successful image load
  const handleImageLoad = () => {
    console.log('Image loaded successfully:', imageSrc);
    setImageLoaded(true);
    setImageError(false);
  };

  // Get heading styles based on screen width
  const getHeadingStyles = () => {
    const baseStyles = {
      fontWeight: '700',
      margin: '5px auto 15px auto',
      textAlign: 'center',
      color: theme.isDarkMode ? '#fff' : '#333',
      textShadow: theme.isDarkMode ? '0 2px 4px rgba(0,0,0,0.3)' : 'none',
      lineHeight: '1.2',
      padding: '0',
      maxWidth: '90%',
      width: 'auto',
      display: 'inline-block',
      position: 'relative'
    };
    
    // Mobile styles
    if (windowWidth < 576) {
      return {
        ...baseStyles,
        fontSize: '20px',
        padding: '0 5px',
        whiteSpace: 'normal',
      };
    }
    
    // Desktop styles  
    return {
      ...baseStyles,
      fontSize: Math.min(Math.max(windowWidth * 0.025, 22), 32) + 'px', // Between 22px and 32px based on viewport
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    };
  };

  return (
    <div style={{ 
      background: theme.colors.background,
      color: theme.colors.text,
      minHeight: '100vh',
      padding: '10px 0'
    }}>
      <div style={{ 
        maxWidth: '800px',
        margin: '0 auto',
        padding: '0 10px'
      }}>
        <header style={{
          marginBottom: '15px',
          textAlign: 'center'
        }}>
          <h1 style={getHeadingStyles()}>
            {getDisplayName()}
          </h1>
          
          {imageSrc ? (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              margin: '15px 0 20px 0'
            }}>
              <div style={{
                padding: '15px',
                background: theme.isDarkMode ? '#2a2a2a' : '#fff',
                borderRadius: '10px',
                boxShadow: theme.isDarkMode ? 
                  '0 3px 8px rgba(0,0,0,0.2)' : 
                  '0 3px 8px rgba(0,0,0,0.1)',
                border: `1px solid ${theme.isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}`,
                maxWidth: '75%'
              }}>
                <img
                  src={imageSrc}
                  alt={getDisplayName()}
                  style={{
                    maxWidth: '100%', 
                    maxHeight: '180px',
                    objectFit: 'contain',
                    display: 'block',
                    margin: '0 auto'
                  }}
                  onError={handleImageError}
                  onLoad={handleImageLoad}
                />
              </div>
            </div>
          ) : (
            <div style={{
              margin: '10px 0 20px 0',
              textAlign: 'center',
              color: theme.colors.textSecondary
            }}>
              <p>No product image available</p>
              {/* Debug information */}
              {process.env.NODE_ENV === 'development' && (
                <div style={{ fontSize: '12px', opacity: 0.7, marginTop: '5px' }}>
                  <p>Debug: Expected image path was {productImage}</p>
                </div>
              )}
            </div>
          )}
        </header>
        
        <SimplePriceComparison 
          specificProductId={productId} 
          csvLookupId={csvLookupId} 
          csvExactMatchOnly={csvExactMatchOnly}
        />
      </div>
    </div>
  );
};

export default SimpleProductPage; 